import Image from 'next/image'
import { ReactElement, useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { withRequiredAuthentication } from '~/main_app/hoc/withRequiredAuthentication'

import useSignUpAnonymously from '~/authentication/hooks/useSignUpAnonymously'
import useTranslate from '~/service_providers/i18n/useTranslate'

import LoadingSpinner from '~/main_app/components/LoadingSpinner'
import { TextWithLink } from '~/main_app/components/TextWithLink'

import { getI18NProvider } from '~/service_providers/i18n/provider'

import ArrowRight from '~/assets/icons/yellow_arrow_right.svg'
import LandingImage from '~/assets/images/landing_image.png'
import Button from '~/main_app/components/Button'

import { useRouter } from 'next/router'
import classes from './styles.module.scss'

function TryItNow (): ReactElement | null {
  const { translate } = useTranslate()
  const { signUpAnonymously } = useSignUpAnonymously()
  const [isSignUpLoading, setIsSignUpLoading] = useState<boolean>(false)
  const i18nProvider = getI18NProvider()
  const currentLanguage = i18nProvider.getLanguage()
  const router = useRouter()
  const onTryItNowClick = useCallback(async () => {
    setIsSignUpLoading(true)

    await toast.promise(
      signUpAnonymously,
      {
        pending: translate('Wait a few seconds while we set up your trial account.'),
        error: translate('Something went wrong. Please try again later.')
      }
    )

    setIsSignUpLoading(false)
    void router.push('/start-options')
  }, [router, signUpAnonymously, translate])

  return (
    <>
      <div className={classes.mainPage}>
        <div className={classes.tryItOutSection}>
          <div className={currentLanguage === 'es' ? classes.esNubelHeader : classes.nubelHeader}>
            <p>
              {translate('Empowering through')}
            </p>
            <span className={classes.simplicity}>
              {translate('simplicity')}
              <span className={classes.dot}>. </span>
            </span>
          </div>
          <span className={classes.mainText}>
            {translate('Streamline your agency with integrated Hiring, Operations, HR, and Payroll solutions.')}
          </span>
          <Button
            variant='primary'
            size={['large', 'content']}
            onClick={async () => { await onTryItNowClick() }}
            className={classes.tryItOutButton}
            iconLeft={isSignUpLoading ? <LoadingSpinner /> : undefined}
            iconRight={<Image src={ArrowRight} width='19' height='15' alt={translate('Arrow Right')} />}
            disabled={isSignUpLoading}
          >
            {translate('Try it now')}
          </Button>
          <TextWithLink
            text={translate('Already have an account? ')}
            linkLabel={translate('Log in')}
            href='/auth/login'
          />
        </div>
        <div className={classes.photoContainer}>
          <Image src={LandingImage} objectFit='cover' alt={translate('Landing Image')} layout='fill' />
        </div>
      </div>
    </>
  )
}

export default withRequiredAuthentication(TryItNow, { invertCondition: true })
